/**
 * Input validation utilities
 * Provides server-side validation schemas and sanitization functions
 */

import { z } from 'zod'

/**
 * Common validation schemas
 */

// Email validation
export const emailSchema = z.string().email('Invalid email address').min(1, 'Email is required')

// Password validation - strong password requirements
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character')

// Name validation
export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(100, 'Name must be less than 100 characters')
  .regex(/^[a-zA-Z\s'-]+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes')

// Phone number validation (US format)
export const phoneSchema = z
  .string()
  .regex(/^\+?1?[-.\s]?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/, 'Invalid phone number format')
  .optional()
  .or(z.literal(''))

// URL validation
export const urlSchema = z.string().url('Invalid URL').optional().or(z.literal(''))

// Zip code validation (US 5-digit)
export const zipCodeSchema = z
  .string()
  .regex(/^\d{5}$/, 'Zip code must be exactly 5 digits')

// Amount/money validation
export const amountSchema = z
  .number()
  .positive('Amount must be positive')
  .max(999999999.99, 'Amount is too large')
  .or(z.string().regex(/^\d+(\.\d{1,2})?$/, 'Invalid amount format').transform(Number))

// Date validation
export const dateSchema = z.string().datetime().or(z.date())

// ID validation (UUID)
export const uuidSchema = z.string().uuid('Invalid ID format')

// Text input sanitization
export function sanitizeText(input: string, maxLength: number = 1000): string {
  return input
    .trim()
    .slice(0, maxLength)
    .replace(/[<>]/g, '') // Remove potential HTML tags
}

// SQL injection prevention (basic)
export function sanitizeForSQL(input: string): string {
  // Remove SQL injection patterns
  return input
    .replace(/['";\\]/g, '') // Remove quotes and backslashes
    .replace(/--/g, '') // Remove SQL comments
    .replace(/\/\*/g, '') // Remove block comments
    .replace(/\*\//g, '')
}

// XSS prevention (basic HTML escaping)
export function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }
  return text.replace(/[&<>"']/g, (m) => map[m])
}

// Rate limiting helper (client-side check, server should enforce)
export class RateLimiter {
  private attempts: Map<string, number[]> = new Map()
  private maxAttempts: number
  private windowMs: number

  constructor(maxAttempts: number = 5, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts
    this.windowMs = windowMs
  }

  check(key: string): boolean {
    const now = Date.now()
    const attempts = this.attempts.get(key) || []
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter((time) => now - time < this.windowMs)
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false // Rate limit exceeded
    }
    
    // Add current attempt
    recentAttempts.push(now)
    this.attempts.set(key, recentAttempts)
    
    return true // Within rate limit
  }

  reset(key: string): void {
    this.attempts.delete(key)
  }
}

// Common form validation schemas
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
})

export const registerSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
})

export const forgotPasswordSchema = z.object({
  email: emailSchema,
})

// Validate and sanitize user input
export function validateAndSanitize<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: z.ZodError } {
  const result = schema.safeParse(data)
  
  if (result.success) {
    return { success: true, data: result.data }
  }
  
  return { success: false, errors: result.error }
}

